import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Update, GetById, GetProductsList } from "adminApi/LaundryProductApi/LaundryProduct.js";
import { validateForm } from "utils/validation";
import { LaundryProductEditRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditLaundryProductForm from "./Componates/EditLaundryProductForm";
import fields from "../json/LaundryProductFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import useFetchAllData from "Hooks/useFetchAllData";

const EditLaundryProduct = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { formdata } = fields;

    const initialValues = formdata.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});

    const { values, errors, handleChange, setErrors, setValues } = useFormChange(
        initialValues,
        LaundryProductEditRules
    );

    // ── Refs to capture original values once after data loads ─────────────
    // original_total = the total_quantity when the record was loaded
    // This is the "budget" the user can redistribute between in_laundry and ready
    const originalTotalRef = useRef(0);
    const originalsSet = useRef(false);

    // Pre-fill form with existing record
    const { loading: loadingFetch } = useFetchFormDataById(id, formdata, GetById, setValues);

    // Capture originals once after data arrives
    useEffect(() => {
        if (originalsSet.current) return;
        const total = parseInt(values.total_quantity) || 0;
        if (total > 0) {
            originalTotalRef.current = total;
            originalsSet.current = true;
        }
    }, [values.total_quantity]);

    // Fetch products_list for the product_name dropdown
    // On edit, the current product's stock is already deducted — the API returns
    // available_quantity which reflects the remaining stock AFTER this record.
    // The dropdown label shows the product name only (no qty confusion on edit).
    const { data: productsList = [], loading: loadingProducts } = useFetchAllData(GetProductsList);

    const productOptions = productsList.map((p) => ({
        value: p.product_name,
        label: p.product_name,
    }));

    // ── When in_laundry_quantity changes → recalculate ready and total ────
    // Rule: in_laundry + ready = original_total (fixed budget)
    // in_laundry cannot exceed original_total
    useEffect(() => {
        if (!originalsSet.current) return;

        const inLaundry = parseInt(values.in_laundry_quantity) || 0;
        const budget = originalTotalRef.current;

        // Clamp: cannot exceed the original total
        if (inLaundry > budget) {
            setValues((prev) => ({
                ...prev,
                in_laundry_quantity: budget,
                ready_quantity: 0,
                total_quantity: budget,
            }));
            return;
        }

        const newReady = budget - inLaundry;
        setValues((prev) => ({
            ...prev,
            ready_quantity: newReady,
            total_quantity: budget,  // total never changes on edit
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.in_laundry_quantity]);

    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => formData.append(key, val ?? ""));
            return await Update({ id, data: formData });
        },
        (vals) => {
            const err = validateForm(vals, LaundryProductEditRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Laundry Product Updated Successfully"
    );

    return (
        <>
            {(loadingSubmit || loadingFetch || loadingProducts) && <Spinner isSpinner />}
            <EditLaundryProductForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                productOptions={productOptions}
                originalTotal={originalTotalRef.current}
            />
        </>
    );
};

export default EditLaundryProduct;
